import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin,
  Phone,
  Clock,
  MessageCircle,
  Search,
  Crosshair,
  Loader2,
  Navigation,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Haversine distance in km
function distanceKm(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function BranchLocator() {
  const [branches, setBranches] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [userQuery, setUserQuery] = useState("");
  const [userLoc, setUserLoc] = useState(null); // {lat, lng, label}
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    api
      .get("/branches")
      .then((r) => {
        setBranches(r.data);
        if (r.data[0]) setSelectedId(r.data[0].id);
      })
      .catch(() => {});
  }, []);

  const selected = useMemo(
    () => branches.find((b) => b.id === selectedId) || branches[0],
    [branches, selectedId]
  );

  // Branch list ordered by distance (only if userLoc set)
  const branchesByDistance = useMemo(() => {
    if (!userLoc || branches.length === 0) return [];
    return branches
      .map((b) => ({
        ...b,
        distance: distanceKm(userLoc.lat, userLoc.lng, b.lat, b.lng),
      }))
      .sort((a, b) => a.distance - b.distance);
  }, [branches, userLoc]);

  const geocode = async (q) => {
    setSearching(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=mx&accept-language=es&q=${encodeURIComponent(
        q + ", Puebla, México"
      )}`;
      const res = await fetch(url);
      const data = await res.json();
      if (!data || data.length === 0) {
        toast.error("No encontramos esa dirección. Intenta ser más específico.");
        return;
      }
      const { lat, lon, display_name } = data[0];
      const loc = { lat: parseFloat(lat), lng: parseFloat(lon), label: display_name };
      setUserLoc(loc);
      // Auto-select closest
      const closest = branches
        .map((b) => ({ ...b, d: distanceKm(loc.lat, loc.lng, b.lat, b.lng) }))
        .sort((a, b) => a.d - b.d)[0];
      if (closest) {
        setSelectedId(closest.id);
        toast.success(
          `Sucursal más cercana: ${closest.name} (${closest.d.toFixed(1)} km).`
        );
      }
    } catch {
      toast.error("No pudimos buscar la dirección. Intenta de nuevo.");
    } finally {
      setSearching(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Tu navegador no soporta geolocalización.");
      return;
    }
    setSearching(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;

        // Reverse geocode for a human-readable address
        let label = "Mi ubicación actual";
        let shortLabel = label;
        try {
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=es&zoom=16`
          );
          if (r.ok) {
            const data = await r.json();
            if (data && data.display_name) {
              label = data.display_name;
              const a = data.address || {};
              const parts = [
                a.road,
                a.house_number,
                a.neighbourhood || a.suburb || a.city_district,
                a.city || a.town || a.village,
              ].filter(Boolean);
              shortLabel = parts.length ? parts.join(", ") : label;
            }
          }
        } catch {
          // Ignore — fall back to coordinates label
        }

        const loc = { lat, lng, label };
        setUserLoc(loc);
        setUserQuery(shortLabel);
        const closest = branches
          .map((b) => ({ ...b, d: distanceKm(loc.lat, loc.lng, b.lat, b.lng) }))
          .sort((a, b) => a.d - b.d)[0];
        if (closest) {
          setSelectedId(closest.id);
          toast.success(
            `Sucursal más cercana: ${closest.name} (${closest.d.toFixed(1)} km).`
          );
        }
        setSearching(false);
      },
      () => {
        toast.error("No pudimos obtener tu ubicación. Revisa permisos.");
        setSearching(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const onSubmitSearch = (e) => {
    e.preventDefault();
    if (!userQuery.trim()) return;
    geocode(userQuery.trim());
  };

  if (!selected) return null;

  const mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(
    selected.map_query || selected.address
  )}&hl=es&t=m&z=15&output=embed&iwloc=near`;

  return (
    <div id="sucursales" className="container-ie py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="locator-section">
      {/* Left: locator UI */}
      <div className="lg:col-span-5">
        <div className="flex items-center gap-3 mb-5">
          <span className="dot-red" />
          <span className="overline" style={{ color: "#c40000" }}>
            Encuentra tu sucursal
          </span>
        </div>
        <h3 className="font-display font-semibold tracking-tight text-2xl md:text-3xl mb-3">
          ¿Cuál te queda más cerca?
        </h3>
        <p className="text-sm text-white/65 max-w-md mb-6">
          Déjanos buscar la sucursal más cercana a ti, o selecciónala directamente.
        </p>

        {/* Search */}
        <form
          onSubmit={onSubmitSearch}
          className="space-y-3 mb-6"
          data-testid="locator-form"
        >
          <div className="relative">
            <Search
              size={14}
              strokeWidth={1.6}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-white/55"
            />
            <input
              data-testid="locator-input"
              type="text"
              placeholder="Tu dirección o colonia (ej. Angelópolis)"
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
              className="w-full bg-white/[0.04] border border-white/15 text-white placeholder:text-white/40 py-3 pr-3 text-sm focus:outline-none focus:border-[#c40000] transition-colors"
              style={{ paddingLeft: "2.25rem" }}
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              data-testid="locator-geo"
              onClick={useMyLocation}
              disabled={searching}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#c40000] text-white text-[0.7rem] font-semibold uppercase tracking-[0.15em] hover:bg-[#a00000] transition-colors disabled:opacity-50"
            >
              {searching ? (
                <Loader2 size={14} strokeWidth={1.8} className="animate-spin" />
              ) : (
                <Crosshair size={14} strokeWidth={1.8} />
              )}
              Mi ubicación
            </button>
            <button
              type="submit"
              data-testid="locator-submit"
              disabled={searching || !userQuery.trim()}
              className="inline-flex items-center justify-center gap-2 px-4 py-3 border border-white/20 text-white text-[0.7rem] font-semibold uppercase tracking-[0.15em] hover:bg-white hover:text-[#0a0a0a] transition-colors disabled:opacity-50"
            >
              <Search size={14} strokeWidth={1.8} /> Encontrar sucursal
            </button>
          </div>
        </form>

        <div className="text-xs uppercase tracking-[0.18em] text-white/55 mb-3">
          O elige una sucursal
        </div>

        {/* Branch chips */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="locator-chips">
          {branches.map((b) => (
            <button
              key={b.id}
              type="button"
              data-testid={`locator-chip-${b.id}`}
              onClick={() => setSelectedId(b.id)}
              className={`text-[0.7rem] uppercase tracking-[0.14em] font-semibold px-3 py-2 border transition-colors ${
                selectedId === b.id
                  ? "bg-[#c40000] text-white border-[#c40000]"
                  : "bg-transparent text-white/85 border-white/15 hover:border-white"
              }`}
            >
              {b.name}
            </button>
          ))}
        </div>

        {/* Ranked branches if user location */}
        <AnimatePresence>
          {userLoc && branchesByDistance.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="mt-7"
              data-testid="locator-results"
            >
              <div className="text-xs uppercase tracking-[0.18em] text-white/55 mb-3">
                Sucursales ordenadas por cercanía
              </div>
              <ul className="space-y-2">
                {branchesByDistance.map((b, i) => (
                  <li key={b.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(b.id)}
                      data-testid={`locator-rank-${b.id}`}
                      className={`w-full text-left flex items-center justify-between gap-3 px-4 py-3 border transition-colors ${
                        selectedId === b.id
                          ? "border-[#c40000] bg-[#c40000]/10"
                          : "border-white/10 hover:border-white/40"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className={`shrink-0 w-7 h-7 flex items-center justify-center text-xs font-bold ${
                            i === 0
                              ? "bg-[#c40000] text-white"
                              : "bg-white/10 text-white/80"
                          }`}
                        >
                          {i + 1}
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate">
                            {b.name}
                          </div>
                          <div className="text-[0.7rem] text-white/55 truncate">
                            {b.address}
                          </div>
                        </div>
                      </div>
                      <span className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-white">
                        <Navigation size={12} strokeWidth={1.8} />
                        {b.distance.toFixed(1)} km
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Right: details + map */}
      <div className="lg:col-span-7">
        <motion.div
          key={selected.id}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5"
          data-testid="locator-details"
        >
          <div className="md:col-span-3 bg-white/[0.04] border border-white/10 p-5">
            <div className="font-display font-semibold text-xl tracking-tight text-white">
              {selected.name}
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-white/75">
              <p className="flex items-start gap-2">
                <MapPin size={14} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                <span>{selected.address}</span>
              </p>
              <p className="flex items-center gap-2">
                <Phone size={14} strokeWidth={1.5} />
                {selected.phone}
              </p>
              <p className="flex items-start gap-2">
                <Clock size={14} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                <span className="leading-tight">
                  {selected.hours.split(" · ").map((h, i) => (
                    <span key={i} className="block whitespace-nowrap">
                      {h}
                    </span>
                  ))}
                </span>
              </p>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <a
                href={`https://wa.me/${selected.whatsapp}?text=${encodeURIComponent(
                  `Hola ${selected.name}, quiero información sobre los cursos.`
                )}`}
                target="_blank"
                rel="noreferrer"
                data-testid="locator-wa"
                className="btn-whatsapp text-[0.7rem] py-2.5 px-4"
              >
                <MessageCircle size={14} strokeWidth={1.8} /> WhatsApp
              </a>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${selected.lat},${selected.lng}`}
                target="_blank"
                rel="noreferrer"
                data-testid="locator-directions"
                className="inline-flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.15em] border border-white/20 py-2.5 px-4 text-white hover:bg-white hover:text-[#0a0a0a] transition-colors"
              >
                <Navigation size={14} strokeWidth={1.8} /> Cómo llegar
              </a>
            </div>
          </div>
        </motion.div>

        <div className="aspect-[16/10] bg-white/5 overflow-hidden border border-white/10">
          <iframe
            key={selected.id}
            title={`Mapa ${selected.name}`}
            data-testid="locator-map"
            src={mapSrc}
            width="100%"
            height="100%"
            style={{ border: 0, filter: "grayscale(0.35) contrast(1.05)" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </div>
  );
}
